import React from 'react';
import {
  FileUp,
  Lock,
  MapPin,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Upload,
  X
} from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '../ui/Card';
import { FieldGroup, FieldHint, FieldLabel, Input } from '../ui/Input';
import MapArea from './MapArea';
import ConfirmUpdateStopDialog from './ConfirmUpdateStopDialog';
import { cn } from '../../lib/cn';
import { useAuth } from '../../hooks/useAuth';

export default function StopsPage({ controller }) {
  const {
    cancelDraggedPosition,
    closeStopForm,
    coordsInput,
    dragPendingStop,
    editingStop,
    editName,
    editPendingMarker,
    editStopId,
    fileInputRef,
    handleCoordsInput,
    handleFileChange,
    handleImport,
    handleSave,
    handleSaveDraggedPosition,
    handleSearchInput,
    handleSelectResult,
    handleUpdate,
    requestUpdateStop,
    stopUpdateConfirm,
    cancelStopUpdateConfirm,
    importError,
    importing,
    importRows,
    importSkipped,
    openCreateStopForm,
    pendingMarker,
    saving,
    searchQuery,
    searchRef,
    searchResults,
    setEditName,
    setEditStopId,
    setFlyTarget,
    setStopId,
    setStopName,
    setVisibleStopIds,
    startEdit,
    stopFormOpen,
    stopId,
    stopName,
    stops,
    switchTab,
    visibleStopIds
  } = controller;

  const { isEditor } = useAuth();
  const [dragActive, setDragActive] = React.useState(false);
  const [customIdMode, setCustomIdMode] = React.useState(false);

  const showEditForm = Boolean(editingStop);
  const showCreateForm = stopFormOpen && !editingStop;
  const hasMapSelection = visibleStopIds.size > 0;
  const recentStops = React.useMemo(
    () =>
      [...stops]
        .sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0))
        .slice(0, 6),
    [stops]
  );

  if (!isEditor) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-10">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-soft p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
            <Lock className="h-5 w-5" />
          </div>
          <h2 className="text-[18px] font-semibold text-slate-900 mt-4">Read-only access</h2>
          <p className="text-[13px] text-slate-500 mt-2">
            Adding or importing stops requires editor permissions. Your account is provisioned as a viewer.
          </p>
          <div className="mt-5">
            <Button variant="primary" size="sm" onClick={() => switchTab('data')}>
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 min-h-0 px-6 lg:px-8 py-6 overflow-auto">
      <div className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-brand-600">
              Stop management
            </p>
            <h1 className="text-[24px] font-semibold tracking-tight text-slate-900 mt-1">
              Add and import stops from one workspace
            </h1>
            <p className="text-[13.5px] text-slate-500 mt-1 max-w-3xl">
              Create stops one-by-one with a map pin, or bulk sync them from a spreadsheet without leaving the dashboard.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" size="sm" onClick={() => switchTab('map')}>
              Open map view
            </Button>
            <Button variant="primary" size="sm" leftIcon={Plus} onClick={openCreateStopForm}>
              {showEditForm ? 'Add another stop' : showCreateForm ? 'New stop' : 'Start adding'}
            </Button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <MiniStat label="Total stops" value={stops.length} tone="emerald" />
          <MiniStat label="Shown on map" value={visibleStopIds.size} tone="brand" />
          <MiniStat label="Ready to import" value={importRows.length} tone="amber" />
        </div>

        <div className="grid gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
          <div className="flex flex-col gap-5 min-w-0">
            <Card
              className={cn(
                showEditForm
                  ? 'border-violet-200 shadow-lift'
                  : showCreateForm
                    ? 'border-brand-200 shadow-lift'
                    : 'border-slate-200'
              )}
            >
              <CardHeader>
                <div className="min-w-0">
                  <p
                    className={cn(
                      'text-[10.5px] font-semibold uppercase tracking-[0.14em]',
                      showEditForm ? 'text-violet-600' : 'text-brand-600'
                    )}
                  >
                    {showEditForm ? 'Editing stop' : 'Single stop'}
                  </p>
                  <CardTitle className="mt-1">
                    {showEditForm
                      ? editingStop.stopName
                      : showCreateForm
                        ? 'Create a new stop'
                        : 'Start a stop draft'}
                  </CardTitle>
                  <CardDescription>
                    {showEditForm
                      ? 'Update the master record and drag the marker if the location changed.'
                      : showCreateForm
                        ? 'Search, enter coordinates, or click the map to place the pin before saving.'
                        : 'Open a draft first, then click the map to place the stop pin.'}
                  </CardDescription>
                </div>

                {(showEditForm || showCreateForm) && (
                  <button
                    type="button"
                    onClick={closeStopForm}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                    aria-label="Close stop form"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </CardHeader>

              {!showEditForm && !showCreateForm ? (
                <CardContent className="pt-0">
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center">
                    <p className="text-[13px] font-medium text-slate-800">No stop draft is open yet.</p>
                    <p className="text-[12px] text-slate-500 mt-1">
                      Start a draft to enable map pinning and save a new stop.
                    </p>
                    <Button variant="primary" size="sm" leftIcon={Plus} className="mt-4" onClick={openCreateStopForm}>
                      Start new stop
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <>
                  <CardContent className="pt-0 flex flex-col gap-4">
                    {showEditForm ? (
                      <>
                        <FieldGroup>
                          <FieldLabel>Stop ID</FieldLabel>
                          <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 h-9">
                            <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            <span className="font-mono text-[12.5px] text-slate-700 truncate">{editStopId || '—'}</span>
                          </div>
                          <FieldHint>The Stop ID is the permanent reference used by routes and can't be changed.</FieldHint>
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Stop name</FieldLabel>
                          <Input
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                            placeholder="Stop name"
                            onKeyDown={(event) => event.key === 'Enter' && requestUpdateStop()}
                            autoFocus
                          />
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Pin location</FieldLabel>
                          {editPendingMarker ? (
                            <PinSummary
                              tone="violet"
                              title="Pending position"
                              lat={editPendingMarker.lat}
                              lng={editPendingMarker.lng}
                            />
                          ) : (
                            <>
                              <PinSummary
                                tone="brand"
                                title="Current position"
                                lat={editingStop.lat}
                                lng={editingStop.lng}
                              />
                              <FieldHint>Click or drag the marker on the map to move the stop.</FieldHint>
                            </>
                          )}
                        </FieldGroup>
                      </>
                    ) : (
                      <>
                        <FieldGroup ref={searchRef}>
                          <FieldLabel>Search location</FieldLabel>
                          <div className="relative">
                            <Input
                              leftIcon={Search}
                              value={searchQuery}
                              onChange={(event) => handleSearchInput(event.target.value)}
                              placeholder="Type a place or address"
                            />
                            {searchResults.length > 0 && (
                              <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-pop animate-slide-up">
                                {searchResults.map((result, index) => (
                                  <button
                                    key={`${result.place_id || result.display_name}-${index}`}
                                    type="button"
                                    className="w-full text-left px-3 py-2 hover:bg-brand-50 transition-colors border-b border-slate-100 last:border-b-0"
                                    onMouseDown={() => handleSelectResult(result)}
                                  >
                                    <p className="text-[13px] font-medium text-slate-900 truncate">
                                      {result.display_name.split(',')[0]}
                                    </p>
                                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                                      {result.display_name.split(',').slice(1, 3).join(',')}
                                    </p>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Or enter coordinates</FieldLabel>
                          <Input
                            value={coordsInput}
                            onChange={(event) => handleCoordsInput(event.target.value)}
                            placeholder="lat, lng - for example 19.218300, 72.978100"
                            inputClassName="font-mono text-[12.5px]"
                          />
                        </FieldGroup>

                        <FieldGroup>
                          <div className="flex items-center justify-between mb-1.5">
                            <FieldLabel className="mb-0">Stop ID</FieldLabel>
                            <button
                              type="button"
                              onClick={() => {
                                const next = !customIdMode;
                                setCustomIdMode(next);
                                if (!next) setStopId('');
                              }}
                              className="text-[11.5px] font-medium text-brand-700 hover:text-brand-800 transition-colors"
                            >
                              {customIdMode ? 'Use auto ID' : 'Use custom code'}
                            </button>
                          </div>
                          {customIdMode ? (
                            <>
                              <Input
                                value={stopId}
                                onChange={(event) => setStopId(event.target.value)}
                                placeholder="Custom code to match an external source"
                                onKeyDown={(event) => event.key === 'Enter' && handleSave()}
                              />
                              <FieldHint>Only set this to match a code from an external/source system.</FieldHint>
                            </>
                          ) : (
                            <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 h-9">
                              <Sparkles className="h-3.5 w-3.5 text-brand-500 shrink-0" />
                              <span className="text-[12.5px] text-slate-500">Assigned automatically on save</span>
                            </div>
                          )}
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Stop name</FieldLabel>
                          <Input
                            value={stopName}
                            onChange={(event) => setStopName(event.target.value)}
                            placeholder="Enter the bus stop name"
                            onKeyDown={(event) => event.key === 'Enter' && handleSave()}
                          />
                        </FieldGroup>

                        <FieldGroup>
                          <FieldLabel>Pin location</FieldLabel>
                          {pendingMarker ? (
                            <PinSummary
                              tone="brand"
                              title="Pending pin"
                              lat={pendingMarker.lat}
                              lng={pendingMarker.lng}
                            />
                          ) : (
                            <FieldHint>
                              Click anywhere on the map once the draft is open to place the stop pin.
                            </FieldHint>
                          )}
                        </FieldGroup>
                      </>
                    )}
                  </CardContent>

                  <CardFooter>
                    <Button variant="ghost" size="md" onClick={closeStopForm}>
                      Cancel
                    </Button>
                    <div className="flex-1" />
                    {showEditForm ? (
                      <Button
                        variant="primary"
                        size="md"
                        leftIcon={Pencil}
                        loading={saving}
                        disabled={!editStopId.trim() || !editName.trim()}
                        onClick={requestUpdateStop}
                      >
                        {saving ? 'Saving' : 'Update stop'}
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="md"
                        loading={saving}
                        disabled={!pendingMarker || !stopName.trim() || (customIdMode && !stopId.trim())}
                        onClick={handleSave}
                      >
                        {saving ? 'Saving' : 'Save stop'}
                      </Button>
                    )}
                  </CardFooter>
                </>
              )}
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <div className="min-w-0">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-600">
                    Bulk import
                  </p>
                  <CardTitle className="mt-1">Import stops from a spreadsheet</CardTitle>
                  <CardDescription>
                    Provide stop name and coordinates — the system assigns each Stop ID automatically. (If a StopID column is present, those codes are kept so route imports keep matching.)
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="pt-0 flex flex-col gap-4">
                <Card className="border-brand-100 bg-gradient-to-br from-brand-50 to-white p-5">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wider text-brand-700 mb-2">
                    Expected columns
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {['Stop name', 'Lat', 'Long'].map((col) => (
                      <span
                        key={col}
                        className="font-mono text-[12px] font-semibold px-2.5 py-1 rounded bg-white border border-brand-200 text-brand-700"
                      >
                        {col}
                      </span>
                    ))}
                    {/* <span className="font-mono text-[12px] px-2.5 py-1 rounded bg-slate-50 border border-slate-200 text-slate-400">
                      StopID (optional)
                    </span> */}
                  </div>
                </Card>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex flex-col items-center justify-center text-center rounded-lg border-2 border-dashed py-8 px-4 transition-all duration-150',
                    'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/30'
                  )}
                >
                  <div className="h-10 w-10 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center mb-2">
                    <FileUp className="h-4 w-4" />
                  </div>
                  <p className="text-[13px] font-semibold text-slate-800">Click or drag a file</p>
                  <p className="text-[12px] text-slate-500 mt-0.5">.xlsx · .xls · .csv</p>
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileChange}
                />

                {importError && (
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] leading-relaxed text-rose-700">
                    {importError}
                  </div>
                )}

                {importRows.length > 0 && (
                  <>
                    <div className="flex flex-col gap-3 px-3 py-3 rounded-lg bg-white border border-slate-200 shadow-soft">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-[20px] font-semibold text-emerald-600 tabular-nums">
                          {importRows.length}
                        </span>
                        <span className="text-[11px] font-medium text-slate-500">valid rows</span>
                      </div>
                      {importSkipped > 0 && (
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[20px] font-semibold text-amber-600 tabular-nums">
                            {importSkipped}
                          </span>
                          <span className="text-[11px] font-medium text-slate-500">skipped</span>
                        </div>
                      )}
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={Upload}
                        loading={importing}
                        onClick={handleImport}
                        className="w-full"
                      >
                        {importing ? 'Importing' : `Import ${importRows.length} stops`}
                      </Button>
                    </div>

                    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-soft">
                      <div className="max-h-[250px] overflow-y-auto">
                        <table className="w-full text-[11.5px]">
                          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5 w-8">#</th>
                              <th className="text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5">Stop ID</th>
                              <th className="text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5">Name</th>
                              <th className="text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5">Lat</th>
                              <th className="text-left font-semibold text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1.5">Long</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importRows.slice(0, 8).map((row, index) => (
                              <tr key={`${row.stopId || row.stopName}-${index}`} className="border-b border-slate-100 last:border-b-0">
                                <td className="px-2 py-1.5 text-slate-400 tabular-nums">{index + 1}</td>
                                <td className="px-2 py-1.5 font-mono text-[10px]">
                                  {row.stopId
                                    ? <span className="text-slate-700">{row.stopId}</span>
                                    : <span className="text-slate-400 italic">auto</span>}
                                </td>
                                <td className="px-2 py-1.5 text-slate-700 font-medium truncate text-[11px]">{row.stopName}</td>
                                <td className="px-2 py-1.5 font-mono text-slate-500 text-[10px]">{row.lat.toFixed(4)}</td>
                                <td className="px-2 py-1.5 font-mono text-slate-500 text-[10px]">{row.lng.toFixed(4)}</td>
                              </tr>
                            ))}
                            {importRows.length > 8 && (
                              <tr>
                                <td colSpan="5" className="px-2 py-1.5 text-center text-slate-400 italic text-[10px]">
                                  and {importRows.length - 8} more
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-5 min-w-0">
            <Card className="overflow-hidden">
              <CardHeader className="items-start lg:items-center">
                <div className="min-w-0">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-600">
                    Placement map
                  </p>
                  <CardTitle className="mt-1">Pin, review, and fine-tune stop locations</CardTitle>
                  <CardDescription>
                    Click the map while a draft is open to place a new stop, or select an existing marker to edit it.
                  </CardDescription>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="info" size="md" dot>
                    {visibleStopIds.size} on map
                  </Badge>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setVisibleStopIds(new Set(stops.map((stop) => stop._id)))}
                    disabled={stops.length === 0}
                  >
                    Show all
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setVisibleStopIds(new Set())}
                    disabled={!hasMapSelection}
                  >
                    Clear stops
                  </Button>
                </div>
              </CardHeader>

              {dragPendingStop && (
                <div className="mx-5 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[13px] font-semibold text-amber-900">Marker moved for {dragPendingStop.stopName}</p>
                    <p className="text-[12px] text-amber-700 mt-0.5">
                      Save the new coordinates or discard the change before leaving the page.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={cancelDraggedPosition}>
                      Discard
                    </Button>
                    <Button variant="primary" size="sm" loading={saving} onClick={handleSaveDraggedPosition}>
                      {saving ? 'Saving' : 'Save position'}
                    </Button>
                  </div>
                </div>
              )}

              <div className="relative h-[480px] border-t border-slate-100">
                <MapArea controller={controller} />
              </div>
            </Card>

            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-600">
                    Recently added
                  </p>
                  <CardTitle className="mt-1">Latest stop records</CardTitle>
                  <CardDescription>
                    Jump back into a stop quickly to review it on the map or update its master data.
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                {recentStops.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-[13px] text-slate-500">
                    No stops are available yet. Create one manually or import a spreadsheet to get started.
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {recentStops.map((stop) => (
                      <div
                        key={stop._id}
                        className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 bg-white"
                      >
                        <span className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                          <MapPin className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-semibold text-slate-900 truncate">{stop.stopName}</p>
                          <p className="text-[11.5px] text-slate-500 font-mono truncate">
                            {stop.stopId} · {stop.lat.toFixed(4)}, {stop.lng.toFixed(4)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setVisibleStopIds(new Set([stop._id]));
                              setFlyTarget({ lat: stop.lat, lng: stop.lng });
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            leftIcon={Pencil}
                            onClick={() => {
                              setVisibleStopIds(new Set([stop._id]));
                              startEdit(stop);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ConfirmUpdateStopDialog
        confirm={stopUpdateConfirm}
        saving={saving}
        onCancel={cancelStopUpdateConfirm}
        onConfirm={handleUpdate}
      />
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const tones = {
    brand: 'border-brand-200 bg-brand-50 text-brand-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700'
  };

  return (
    <div className={cn('rounded-xl border px-4 py-3 shadow-soft', tones[tone])}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">{label}</p>
      <p className="text-[26px] font-semibold tracking-tight mt-1 tabular-nums">{value}</p>
    </div>
  );
}

function PinSummary({ title, lat, lng, tone }) {
  const tones = {
    brand: 'border-brand-200 bg-brand-50 text-brand-800',
    violet: 'border-violet-200 bg-violet-50 text-violet-800'
  };

  return (
    <div className={cn('flex items-start gap-2 rounded-md px-3 py-2 border', tones[tone])}>
      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium opacity-80">{title}</p>
        <p className="font-mono text-[12px] mt-0.5">
          {lat.toFixed(6)}, {lng.toFixed(6)}
        </p>
      </div>
    </div>
  );
}
